import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import Loader from "../../UI/Loader";
import "jspdf-autotable";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaFileExport, FaPrint, FaTimes } from 'react-icons/fa';
import AuthContext from "../../../store/auth-context";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { exportTableToExcel } from "../../../util/excelExportUtil";
import { Spinner } from "react-bootstrap";

const WaitingListReport = () => {
  const { t } = useTranslation();
  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [showPreview, setShowPreview] = useState(false);
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [exportFormat, setExportFormat] = useState("excel");
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        const token = getAuthToken();
        if (token === null || token === 'EXPIRED') {
          navigate('/');
          return;
        }

        const apiChar = getApiChara();
        const apiUrl = `${apiChar}/report/getTodaysWaitingListReport`;

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: "Bearer " + token,
          }
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        if (!result) {
          throw new Error("JSON data is empty");
        }


        setData(result);
      } catch (error) {
        setIsError(true);
        setError(error.message || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const TableHeader = () => (
    <thead className="table-secondary">
      <tr>
        <th>{t("Sr No")}</th>
        <th>{t("Waiting Id")}</th>
        <th>{t("Patient Id")}</th>
        <th>{t("Name")}</th>
        <th>{t("Mobile")}</th>
        <th>{t("Appointment")}</th>
        <th>{t("Doctor")}</th>
      </tr>
    </thead>
  );

  const onPrintHandler = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);

    // Calculate the center position based on the page width
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const textWidth =
      (pdfDoc.getStringUnitWidth("Waiting List Reports") *
        pdfDoc.internal.getFontSize()) /
      pdfDoc.internal.scaleFactor;
    const xPos = (pageWidth - textWidth) / 2;

    pdfDoc.setFontSize(8);
    // pdfDoc.text("Waiting List Reports", xPos, 20);
    const tableClone = document.querySelector("#waitingTable").cloneNode(true);
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader) actionHeader.remove();
    tableClone.querySelectorAll("tr").forEach(tr => {
      const lastCell = tr.querySelector("td:last-child");
      if (lastCell) lastCell.remove();
    });

    const startY = drawReportHeader(pdfDoc, hospitalData, "Waiting List Reports", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

    pdfDoc.autoTable({
      head: [["Sr No", "Waiting Id", "Patient Id", "Name", "Mobile", "Appointment", "Doctor"]],
      body: data.map((item, index) => [
        index + 1,
        item.waitingListId || "-",
        item.patientId?.patientId || "-",
        `${item.patientId?.firstName || ""} ${item.patientId?.lastName || ""}`.trim(),
        item.patientId?.mobile || "-",
        new Date(item.appointmentTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        item.doctorId?.firstName ? `${item.doctorId.firstName} ${item.doctorId.lastName}` : "-"
      ]),
      startY: startY + 10,
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);
    pdfDoc.save("waitingreport.pdf");
  };

  const handleExport = () => {
    if (exportFormat === "excel") {
      setIsExporting(true);
      setTimeout(() => {
        exportTableToExcel("waitingTable", "Waiting_List_Report");
        setIsExporting(false);
      }, 500);
    } else {
      onPrintHandler();
    }
  };

  const onPreviewHandler = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);

    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const textWidth =
      (pdfDoc.getStringUnitWidth("Waiting List Reports") *
        pdfDoc.internal.getFontSize()) /
      pdfDoc.internal.scaleFactor;
    const xPos = (pageWidth - textWidth) / 2;

    pdfDoc.setFontSize(10);
    // pdfDoc.text("Waiting List Reports", xPos, 20);
    const startY = drawReportHeader(pdfDoc, hospitalData, "Waiting List Reports", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

    pdfDoc.autoTable({
      head: [["Sr No", "Waiting Id", "Patient Id", "Name", "Mobile", "Appointment", "Doctor"]],
      body: data.map((item, index) => [
        index + 1,
        item.waitingListId || "-",
        item.patientId?.patientId || "-",
        `${item.patientId?.firstName || ""} ${item.patientId?.lastName || ""}`.trim(),
        item.patientId?.mobile || "-",
        new Date(item.appointmentTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        item.doctorId?.firstName ? `${item.doctorId.firstName} ${item.doctorId.lastName}` : "-"
      ]),
      startY: startY + 10,
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);

    // Create a Blob containing the PDF data
    const pdfBlob = pdfDoc.output("blob");

    // Create a URL for the Blob
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Open the modal with the PDF preview
    setShowPreview(pdfUrl);
  };
  const onGetPrintHandler = () => {
    if (!data || data.length === 0) {
      alert('No data to print');
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableToPrint = document.getElementById('waitingTable');
    const tableClone = tableToPrint.cloneNode(true);
    // Remove Action Column from Clone
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader && actionHeader.innerText.toLowerCase().includes("action")) {
      actionHeader.remove();
      tableClone.querySelectorAll("tr").forEach(tr => {
        const lastCell = tr.querySelector("td:last-child");
        if (lastCell) lastCell.remove();
      });
    }

    const printContent = `
      <html>
        <head>
          <title>Waiting List Report - ${new Date().toLocaleDateString()}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, "Today's Waiting List Report")}
          <div class="meta-info">
            <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
            <div><strong>Total Patients:</strong> ${data.length}</div>
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };
  return (
    <>
      {isLoading && <Loader />}
      <div className="container-fluid">
        <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center">
          <h5 className="textReports mb-0">{t("Today's Waiting List Report")}</h5>
        </div>
        <div className="row">
          {isError && data && data.length === 0 && (
            <p>Error: {error}</p>
          )}
          {data && data.length > 0 && (
            <div className="printData boxes table-responsive">
              <Table id="waitingTable" striped bordered hover>
                <TableHeader />
                <tbody>
                  {data.map((waitingp) => (
                    <tr key={waitingp?.waitingListId}>
                      <td>{waitingp?.serialNo}</td>
                      <td>{waitingp?.waitingListId}</td>
                      <td>{waitingp?.patientId?.patientId}</td>
                      <td>
                        {waitingp?.patientId?.firstName +
                          " " +
                          (waitingp?.patientId?.middleName ? waitingp.patientId?.middleName + " " : "") +
                          waitingp?.patientId?.lastName}
                      </td>
                      <td>{waitingp?.patientId?.mobile}</td>
                      <td>
                        {new Date(waitingp?.appointmentTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td>
                        {waitingp?.doctorId?.firstName && waitingp?.doctorId?.lastName
                          ? `${waitingp?.doctorId?.firstName} ${waitingp?.doctorId?.lastName}`
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {data && data.length === 0 && (
            <div className="printData boxes table-responsive">
              <Table striped bordered hover>
                <TableHeader />
                <tbody>
                  <tr>
                    <td colSpan="7" className="text-center">
                      {t("No Record found")}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </div>
        {data && data.length > 0 && (
          <div id="options" className="mt-4">
            <div className="row options mt-4 bg-light p-3 rounded shadow-sm border mx-0 align-items-end">


              <div className="row g-3 justify-content-center align-items-end">
                <div className="col-auto">
                  <label className="fw-bold small d-block">{t("Select Size")}</label>
                  <select
                    className="form-select"
                    value={selectedPageSize}
                    onChange={(e) => setSelectedPageSize(e.target.value)}
                    style={{ width: '100px' }}
                  >
                    <option value="a2">A2</option>
                    <option value="a3">A3</option>
                    <option value="a4">A4</option>
                    <option value="a5">A5</option>
                  </select>
                </div>

                <div className="col-auto">
                  <label className="fw-bold small d-block">{t("Orientation")}</label>
                  <select
                    className="form-select"
                    value={selectedOrientation}
                    onChange={(e) => setSelectedOrientation(e.target.value)}
                    style={{ width: '130px' }}
                  >
                    <option value="portrait">{t("Portrait")}</option>
                    <option value="landscape">{t("Landscape")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <button onClick={onGetPrintHandler} className="btn-premium">
                    <FaPrint className="me-2" /> {t("PRINT")}
                  </button>
                </div>

                <div className="col-auto">
                  <button onClick={onPreviewHandler} className="btn-premium">
                    <FaEye className="me-2" /> {t("PREVIEW")}
                  </button>
                </div>

                <div className="col-auto">
                  <label className="fw-bold small d-block">{t("Format")}</label>
                  <select
                    className="form-select"
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    style={{ width: '140px' }}
                  >
                    <option value="pdf">{t("PDF Format")}</option>
                    <option value="excel">{t("Excel Format")}</option>
                  </select>
                </div>

                <div className="col-auto">
                  <button
                    onClick={handleExport}
                    className="btn-export-premium d-flex align-items-center"
                    disabled={isExporting}
                  >
                    {isExporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaFileExport className="me-2" />}
                    {isExporting ? t("EXPORTING...") : t("EXPORT")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showPreview && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header bg-premium text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <FaEye className="me-2" /> {t("WAITING LIST REPORT PREVIEW")}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-link text-white p-0 border-0"
                    onClick={() => setShowPreview(null)}
                    aria-label="Close"
                    style={{ fontSize: "1.5rem", lineHeight: "1" }}
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body p-0 bg-dark">
                  <iframe
                    title="PDF Preview"
                    src={showPreview}
                    width="100%"
                    height="750"
                    frameBorder="0"
                    className="w-100"
                  ></iframe>
                </div>
                <div className="modal-footer bg-light p-2">
                  <Button className="btn-secondary-premium" onClick={() => setShowPreview(null)}>
                    {t("CLOSE PREVIEW")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div >
    </>
  );
};

export default WaitingListReport;